import React, { useEffect, useMemo, useState } from "react";
import { AuditLogs } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { formatAuditDate, formatAuditTime } from "../lib/auditFormat";
import { cn } from "../lib/utils";
import {
  Activity, FileUp, FileCheck, CheckCircle2, Pause, Play,
  ShieldCheck, Receipt, Hash, Sparkles, Truck, Pencil, Banknote, AlertOctagon,
  ClipboardList, Wrench, Package, ChevronDown, ChevronUp, Zap, Wallet, Trash2,
} from "lucide-react";

/**
 * Activity Timeline — collapsible, department-filterable audit feed.
 *
 * Default UX:
 *  • Compact mode: shows latest 3 activities (2 on mobile) of the latest 10
 *    loaded records.
 *  • "View All Activities" → expands to show the complete history. If we still
 *    have only the initial 10, a second fetch pulls up to 500 rows.
 *  • Quick filters along the header chip-row let users scope by department:
 *    All / Sales / PDI / Accounts / Back Office / Spares / Delivery.
 *
 * Spec ref: keep the panel compact & operationally useful while preserving
 * full audit history when required.
 */

// ─────────── Action type → display metadata + department ───────────
const ACTION_META = {
  // Sales (DSE / customer-facing)
  sale_created:           { icon: ClipboardList,  tone: "primary", dept: "sales" },
  sale_submitted:         { icon: CheckCircle2,   tone: "emerald", dept: "sales" },
  sale_deleted:           { icon: AlertOctagon,   tone: "rose",    dept: "sales" },
  vin_updated:            { icon: Pencil,         tone: "amber",   dept: "sales" },
  delivery_date_updated:  { icon: Pencil,         tone: "amber",   dept: "sales" },
  document_uploaded:      { icon: FileUp,         tone: "primary", dept: "sales" },
  payment_receipts_updated: { icon: Wallet,       tone: "primary", dept: "sales" },
  payment_receipt_deleted:  { icon: Trash2,       tone: "rose",    dept: "sales" },
  // PDI
  vin_assigned:           { icon: Wrench,         tone: "amber",   dept: "pdi" },
  pdi_marked_ready:       { icon: CheckCircle2,   tone: "emerald", dept: "pdi" },
  // Accounts
  accounts_completed:     { icon: Banknote,       tone: "emerald", dept: "accounts" },
  billing_ledger_updated: { icon: Pencil,         tone: "amber",   dept: "accounts" },
  // Back Office
  insurance_completed:    { icon: ShieldCheck,    tone: "emerald", dept: "backoffice" },
  insurance_skipped:      { icon: ShieldCheck,    tone: "slate",   dept: "backoffice" },
  road_tax_completed:     { icon: Receipt,        tone: "emerald", dept: "backoffice" },
  road_tax_submitted_to_rto:   { icon: Receipt,   tone: "info",    dept: "backoffice" },
  road_tax_unsubmitted_from_rto: { icon: Receipt, tone: "amber",   dept: "backoffice" },
  registration_updated:   { icon: Receipt,        tone: "amber",   dept: "backoffice" },
  hsrp_updated:           { icon: Hash,           tone: "emerald", dept: "backoffice" },
  vas_completed:          { icon: Sparkles,       tone: "emerald", dept: "backoffice" },
  vas_skipped:            { icon: Sparkles,       tone: "slate",   dept: "backoffice" },
  // Spares
  spares_completed:       { icon: Package,        tone: "emerald", dept: "spares" },
  // Delivery / transit
  sale_delivered:         { icon: Truck,          tone: "emerald", dept: "delivery" },
  transit_hold_activated: { icon: Truck,          tone: "info",    dept: "delivery" },
  transit_hold_released:  { icon: Truck,          tone: "emerald", dept: "delivery" },
  // Early delivery approval workflow
  early_delivery_requested: { icon: AlertOctagon, tone: "amber",   dept: "delivery" },
  early_delivery_approved:  { icon: CheckCircle2, tone: "emerald", dept: "delivery" },
  early_delivery_rejected:  { icon: AlertOctagon, tone: "rose",    dept: "delivery" },
  // Generic task lifecycle (ungrouped)
  task_completed:         { icon: FileCheck,      tone: "emerald" },
  task_delay_marked:      { icon: AlertOctagon,   tone: "rose" },
  task_delay_cleared:     { icon: CheckCircle2,   tone: "emerald" },
  sla_paused:             { icon: Pause,          tone: "info" },
  sla_resumed:            { icon: Play,           tone: "emerald" },
};

const TONE_CLS = {
  primary:  "border-primary/20 bg-primary/5 text-primary",
  emerald:  "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber:    "border-amber-200 bg-amber-50 text-amber-700",
  rose:     "border-rose-200 bg-rose-50 text-rose-700",
  info:     "border-blue-200 bg-blue-50 text-blue-700",
  slate:    "border-slate-200 bg-slate-50 text-slate-600",
};

const DEPT_FILTERS = [
  { value: "all",        label: "All" },
  { value: "sales",      label: "Sales" },
  { value: "pdi",        label: "PDI" },
  { value: "accounts",   label: "Accounts" },
  { value: "backoffice", label: "Back Office" },
  { value: "spares",     label: "Spares" },
  { value: "delivery",   label: "Delivery" },
];

const INITIAL_LIMIT = 10;
const EXPANDED_LIMIT = 500;
const COMPACT_COUNT = 3;
const COMPACT_COUNT_MOBILE = 2;

function deptOf(actionType) {
  return ACTION_META[actionType]?.dept || "other";
}

function ChangeChip({ before, after }) {
  const bn = before === null || before === undefined || before === "";
  const an = after === null || after === undefined || after === "";
  if (bn && an) return null;
  if (String(before) === String(after)) return null;
  if (bn) {
    return (
      <span className="ml-1 inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
        Set: <span className="font-mono-data">{String(after)}</span>
      </span>
    );
  }
  return (
    <span className="ml-1 inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200">
      <span className="font-mono-data line-through opacity-60">{String(before)}</span>
      <span className="text-slate-400">→</span>
      <span className="font-mono-data">{String(after)}</span>
    </span>
  );
}

export default function ActivityTimeline({ saleId, refreshKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [hasFullHistory, setHasFullHistory] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dept, setDept] = useState("all");
  const [loadingMore, setLoadingMore] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Initial fast load — only 10 records for the collapsed feed.
  useEffect(() => {
    let cancelled = false;
    setExpanded(false);
    setHasFullHistory(false);
    (async () => {
      if (!saleId) return;
      try {
        const data = await AuditLogs.list({ sale_id: saleId, limit: INITIAL_LIMIT });
        if (!cancelled) {
          setRows(data);
          setHasFullHistory(data.length < INITIAL_LIMIT);
        }
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [saleId, refreshKey]);

  const loadFullHistory = async () => {
    if (hasFullHistory || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await AuditLogs.list({ sale_id: saleId, limit: EXPANDED_LIMIT });
      setRows(data);
      setHasFullHistory(true);
    } catch {
      // keep current rows; user can retry
    } finally {
      setLoadingMore(false);
    }
  };

  const onToggle = async () => {
    if (!expanded) {
      await loadFullHistory();
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  };

  // Dept-filtered rows (newest first comes from the API).
  const filtered = useMemo(() => {
    if (!rows) return [];
    if (dept === "all") return rows;
    return rows.filter((r) => deptOf(r.action_type) === dept);
  }, [rows, dept]);

  const compactCount = isMobile ? COMPACT_COUNT_MOBILE : COMPACT_COUNT;
  const visible = expanded ? filtered : filtered.slice(0, compactCount);

  const total = rows ? rows.length : 0;
  const showingCount = visible.length;
  const headerSummary = expanded
    ? `Showing ${filtered.length} ${filtered.length === 1 ? "activity" : "activities"}`
    : `Latest ${showingCount} of ${total}${hasFullHistory ? "" : "+"} Activities`;

  // Hide the toggle entirely when there's nothing more to reveal.
  const moreAvailable = !expanded
    ? filtered.length > compactCount || !hasFullHistory
    : true;

  return (
    <Card className="border-slate-200" data-testid="activity-timeline">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Zap className="h-3.5 w-3.5" />
          </span>
          <div>
            <div className="font-display text-sm font-semibold text-slate-900">
              Activity Timeline
            </div>
            <div className="text-[11px] text-slate-500" data-testid="timeline-summary">
              {headerSummary}
            </div>
          </div>
        </div>
        {/* Quick dept filters (compact chip-row) */}
        <div className="flex flex-wrap gap-1" data-testid="timeline-dept-filters">
          {DEPT_FILTERS.map((d) => (
            <button
              key={d.value}
              onClick={() => setDept(d.value)}
              className={cn(
                "rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 transition",
                dept === d.value
                  ? "bg-primary text-white ring-primary"
                  : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50",
              )}
              data-testid={`timeline-filter-${d.value}`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <CardContent className="p-0">
        {rows === null ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">Loading audit trail…</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="timeline-empty">
            {dept === "all"
              ? "No activity captured yet."
              : "No activity for this department."}
          </div>
        ) : (
          <ol className="divide-y divide-border" data-testid="timeline-rows">
            {visible.map((e) => (
              <TimelineRow key={e.id} entry={e} compact={!expanded} />
            ))}
          </ol>
        )}

        {/* Sticky toggle at the bottom — visible on mobile and desktop */}
        {moreAvailable && filtered.length > 0 && (
          <div
            className="sticky bottom-0 z-10 flex justify-center border-t border-slate-100 bg-white/95 px-5 py-2 backdrop-blur"
            data-testid="timeline-toggle-wrap"
          >
            <button
              type="button"
              onClick={onToggle}
              disabled={loadingMore}
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition",
                expanded
                  ? "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                  : "bg-primary/10 text-primary ring-primary/20 hover:bg-primary/15",
              )}
              data-testid="timeline-toggle"
            >
              {loadingMore ? (
                "Loading…"
              ) : expanded ? (
                <>
                  <ChevronUp className="h-3 w-3" /> Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> View All Activities
                </>
              )}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TimelineRow({ entry, compact }) {
  const meta = ACTION_META[entry.action_type] || { icon: Activity, tone: "slate" };
  const Icon = meta.icon;
  return (
    <li
      className={cn(
        "flex items-start gap-3 px-5",
        compact ? "py-2" : "py-3.5",
      )}
      data-testid={`audit-row-${entry.action_type}`}
    >
      <div
        className={cn(
          "flex-shrink-0 rounded-md border",
          compact ? "mt-0.5 flex h-6 w-6 items-center justify-center" : "mt-0.5 flex h-7 w-7 items-center justify-center",
          TONE_CLS[meta.tone],
        )}
      >
        <Icon className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span
            className={cn(
              "font-semibold text-slate-900",
              compact ? "text-[13px]" : "text-sm",
            )}
          >
            {entry.action_label}
          </span>
          {!compact && <ChangeChip before={entry.before_value} after={entry.after_value} />}
        </div>
        <div
          className={cn(
            "mt-0.5 text-slate-500",
            compact ? "text-[10px]" : "text-xs",
          )}
        >
          {entry.user_name ? (
            <>
              <span className="font-medium text-slate-700">{entry.user_name}</span>
              {entry.user_role && <span className="text-slate-400"> ({entry.user_role})</span>}
            </>
          ) : (
            <span className="italic text-slate-400">System</span>
          )}
          <span className="mx-1.5 text-slate-300">·</span>
          <span className="font-mono-data">
            {formatAuditDate(entry.created_at)} {formatAuditTime(entry.created_at)}
          </span>
        </div>
      </div>
    </li>
  );
}
